import React, { useEffect, useState, useMemo, useCallback, memo } from 'react'
import { Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { getBorder } from 'utils/Styles'
import AddIcon from '@mui/icons-material/Add'
import ListTransfer from 'components/transfer/ListTransfer'
import CloseIcon from '@mui/icons-material/Close'

import { gettingPaycodes } from 'utils/Paycodes'

// Componente AddPaycode optimizado con memo
const AddPaycode = memo(({ open, setOpen, loading, allItems, right, setRight, updateRows }) => {
    const { t } = useTranslation()

    const handleAssign = useCallback(() => {
        updateRows(right)
        setOpen(false)
    }, [right, updateRows, setOpen])

    const handleCancel = useCallback(() => {
        setOpen(false)
    }, [setOpen])

    if (!open) return null // Evitar renderizado innecesario

    return (
        <Dialog open={open} maxWidth='md' fullWidth>
            <DialogTitle>
                <Typography variant='body3'>
                    {t('settings.paycodes.table.modals.add.title')}
                </Typography>
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => handleCancel()}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <ListTransfer
                            loading={loading}
                            allItems={allItems}
                            right={right}
                            setRight={setRight}
                            textLeft={t('settings.paycode_distribution.dialog.inputs.add_table.columns.paycode_available')}
                            textRight={t('settings.paycode_distribution.dialog.inputs.add_table.columns.paycode_selected')}
                            textSelected={t('settings.paycode_distribution.dialog.inputs.add_table.columns.selected')}
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ pb: 2, pr: 3 }}>
                <Button
                    variant='contained'
                    disableElevation
                    color='error'
                    onClick={handleCancel}
                >
                    {t('general.buttons.cancel')}
                </Button>

                <Button
                    variant='contained'
                    disableElevation
                    color='primary'
                    onClick={handleAssign}
                >
                    {t('general.buttons.assign')}
                </Button>
            </DialogActions>
        </Dialog>
    )
})

AddPaycode.displayName = 'AddPaycode'

// Componente CrossCell optimizado
const CrossCell = memo(({ cell, crossCells, setCrossCells, loading, setOpen, setRight, setCellChecked }) => {
    const isChecked = cell?.checked || false

    const handleCheckboxChange = useCallback((event) => {
        const newChecked = event.target.checked

        setCrossCells(prevCells =>
            prevCells.map(c =>
                c.zone_deviation === cell.zone_deviation && c.overtime === cell.overtime
                    ? { ...c, checked: newChecked }
                    : c
            )
        )
    }, [cell, setCrossCells])

    const handleAddClick = useCallback(() => {
        setRight(cell.paycodes || [])
        setCellChecked([cell])
        setOpen(true)
    }, [cell, setRight, setCellChecked, setOpen])

    const paycodesDisplay = useMemo(() => {
        if (!cell?.paycodes?.length) return 0

        return (
            <Box sx={{
                width: '100%', // Ancho fijo para el contenedor de texto
                textAlign: 'center',
            }}>
                {cell.paycodes.map((paycode, index) => (

                    <Chip
                        key={index}
                        label={paycode.name}
                        size='small'
                        variant="outlined"
                        sx={{ my: 0.5 }}
                    />
                ))}
            </Box>
        )
    }, [cell?.paycodes])

    return (
        <TableCell
            align="center"
            sx={{
                borderLeft: (theme) => getBorder(theme.palette.mode),
                wordWrap: 'break-word',
                overflow: 'hidden',
                width: 'auto',
                maxWidth: 'auto', // Ancho máximo para la celda
            }}
        >
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                justifyContent: 'space-between',
            }}>
                <Checkbox
                    checked={isChecked}
                    onChange={handleCheckboxChange}
                />
                {paycodesDisplay}
                <IconButton
                    disabled={loading}
                    color='success'
                    onClick={handleAddClick}
                >
                    <AddIcon />
                </IconButton>
            </Box>
        </TableCell>
    )
})

CrossCell.displayName = 'CrossCell'

// Hook personalizado para manejar la lógica de celdas
const useCrossCells = (overtimes, zonesDeviations, paycodesDistribution, paycodes) => {
    return useMemo(() => {
        if (!overtimes.length || !zonesDeviations.length || !paycodes.length) {
            return []
        }

        const cells = []

        overtimes.forEach((overtime, column) => {
            zonesDeviations.forEach((zoneDeviation, row) => {
                const paycodesForCell = paycodesDistribution
                    .filter(dp =>
                        dp.paycode_distribution_zone_schedule.toString() === zoneDeviation.uuid.toString() &&
                        dp.paycode_distribution_overtime.toString() === overtime.uuid.toString()
                    )
                    .map(dp => dp.paycode_id)

                const matchingPaycodes = paycodes
                    .filter(p => paycodesForCell.includes(p.id))
                    .map(p => ({ id: p.id, name: p.name }))

                const cell = {
                    zone_deviation: zoneDeviation.name,
                    overtime: overtime.name,
                    column,
                    row,
                    paycodes: matchingPaycodes,
                    checked: false,
                    hidden: false,
                    paycode_distribution_zone_schedule: zoneDeviation.uuid,
                    paycode_distribution_overtime: overtime.uuid
                }

                cells.push(cell)
            })
        })

        return cells
    }, [overtimes, zonesDeviations, paycodesDistribution, paycodes])
}

// Componente principal optimizado
const AssignmentTable = ({
    overtimes = [], // Valores por defecto
    zones_desviations = [], // Corregido nombre (mantuve original para compatibilidad)
    paycodesDistribution = [],
    crossCells,
    setCrossCells
}) => {
    const { t } = useTranslation()

    const [loadingGettingPaycodes, setLoadingGettingPaycodes] = useState(false)
    const [open, setOpen] = useState(false)
    const [paycodes, setPaycodes] = useState([])
    const [checked, setChecked] = useState([])
    const [right, setRight] = useState([]) // Corregido typo

    // Generar celdas usando el hook personalizado
    const generatedCells = useCrossCells(overtimes, zones_desviations, paycodesDistribution, paycodes)

    // Función optimizada para actualizar filas
    const updateRows = useCallback((newPaycodes) => {
        setRight([])

        setCrossCells(prevCells =>
            prevCells.map(cell => {
                const shouldUpdate = checked.some(c =>
                    c.paycode_distribution_zone_schedule.toString() === cell.paycode_distribution_zone_schedule.toString() &&
                    c.paycode_distribution_overtime.toString() === cell.paycode_distribution_overtime.toString()
                )

                return shouldUpdate ? { ...cell, paycodes: newPaycodes } : cell
            })
        )
    }, [checked, setCrossCells])

    // Función para manejar selección múltiple
    const handleBulkAssign = useCallback(() => {
        const checkedCells = crossCells.filter(cell => cell.checked)
        setChecked(checkedCells)
        setRight([])
        setOpen(true)
    }, [crossCells])

    // Función para manejar checkboxes de columna
    const handleOvertimeCheckbox = useCallback((overtimeName, isChecked) => {
        setCrossCells(prevCells =>
            prevCells.map(c =>
                c.overtime === overtimeName ? { ...c, checked: isChecked } : c
            )
        )
    }, [setCrossCells])

    // Función para manejar checkboxes de fila
    const handleZoneCheckbox = useCallback((zoneName, isChecked) => {
        setCrossCells(prevCells =>
            prevCells.map(c =>
                c.zone_deviation === zoneName ? { ...c, checked: isChecked } : c
            )
        )
    }, [setCrossCells])

    // Calcular estados de checkboxes para optimizar renders
    const overtimeCheckboxStates = useMemo(() => {
        return overtimes.reduce((acc, overtime) => {
            const overtimeCells = crossCells.filter(cell => cell.overtime === overtime.name)
            const checkedCount = overtimeCells.filter(cell => cell.checked).length

            acc[overtime.name] = {
                checked: overtimeCells.length > 0 && checkedCount === overtimeCells.length,
                indeterminate: checkedCount > 0 && checkedCount < overtimeCells.length
            }

            return acc
        }, {})
    }, [crossCells, overtimes])

    const zoneCheckboxStates = useMemo(() => {
        return zones_desviations.reduce((acc, zone) => {
            const zoneCells = crossCells.filter(cell => cell.zone_deviation === zone.name)
            const checkedCount = zoneCells.filter(cell => cell.checked).length

            acc[zone.name] = {
                checked: zoneCells.length > 0 && checkedCount === zoneCells.length,
                indeterminate: checkedCount > 0 && checkedCount < zoneCells.length
            }

            return acc
        }, {})
    }, [crossCells, zones_desviations])

    // Efectos optimizados
    useEffect(() => {
        gettingPaycodes(setLoadingGettingPaycodes, setPaycodes, "paycode_distribution")
    }, [])

    useEffect(() => {
        if (generatedCells.length > 0) {
            setCrossCells(generatedCells)
        }
    }, [generatedCells, setCrossCells])

    const hasCheckedCells = crossCells.some(cell => cell.checked)

    const getColorByIndex = useCallback((data) => {
        const colors = ['primary', 'warning', ''];

        return (
            <>
                {Array.isArray(data) && data.map((item, index) => {
                    const colorIndex = item.zone_id ? 0 : item.schedule_desviation_rule_id ? 1 : 2;
                    let name = item.name === 'default' ? t('settings.paycode_distribution.dialog.inputs.add_table.columns.predetermined') : item.name;

                    return (
                        <React.Fragment key={item.name}>
                            {index > 0 && <span style={{ margin: '0 4px' }}>{' + '}</span>}
                            <Chip
                                label={name}
                                color={colors[colorIndex]}
                                variant="outlined"
                                size="small"
                            />
                        </React.Fragment>
                    );
                })}
            </>
        );
    }, [t]);


    return (
        <>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} sx={{ mt: 1.5 }}>
                <TableContainer
                    sx={{
                        background: 'transparent',
                        boxShadow: 'none',
                        border: (theme) => getBorder(theme.palette.mode),
                    }}
                    component={Paper}
                >
                    <Table size='small'>
                        <TableHead>
                            <TableRow>
                                <TableCell colSpan={2} sx={{ borderRight: (theme) => getBorder(theme.palette.mode) }} />
                                <TableCell colSpan={overtimes.length} align="center">
                                    <Typography variant='body2'>
                                        {t('settings.paycode_distribution.dialog.inputs.add_table.columns.overtime')}
                                    </Typography>
                                </TableCell>
                            </TableRow>

                            <TableRow>
                                <TableCell>
                                    <Box display="flex" justifyContent="center">
                                        <Button
                                            disabled={!hasCheckedCells}
                                            onClick={handleBulkAssign}
                                        >
                                            {t('settings.paycode_distribution.dialog.inputs.add_table.columns.assign')}
                                        </Button>
                                    </Box>
                                </TableCell>

                                <TableCell
                                    align="center"
                                    sx={{ borderLeft: (theme) => getBorder(theme.palette.mode) }}

                                >
                                    <Typography variant='body2'>
                                        {t('settings.paycode_distribution.dialog.inputs.add_table.columns.zone_schedule_desviation')}
                                    </Typography>
                                </TableCell>

                                {overtimes.map((overtime) => {
                                    const checkboxState = overtimeCheckboxStates[overtime.name] || {}

                                    return (
                                        <TableCell
                                            key={overtime.uuid || overtime.name}
                                            align="center"
                                            sx={{ borderLeft: (theme) => getBorder(theme.palette.mode) }}
                                        >
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'flex-start',
                                                width: '100%',
                                                maxWidth: '100%',
                                            }}>
                                                <Checkbox
                                                    checked={checkboxState.checked || false}
                                                    indeterminate={checkboxState.indeterminate || false}
                                                    onChange={(event) => handleOvertimeCheckbox(overtime.name, event.target.checked)}
                                                />
                                                <Typography variant='subtitle1'>{overtime.name}</Typography>
                                            </Box>
                                        </TableCell>
                                    )
                                })}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {zones_desviations.map((zd) => {
                                const checkboxState = zoneCheckboxStates[zd.name] || {}

                                return (
                                    <TableRow id='zd-row'  key={zd.uuid || zd.name}>
                                        <TableCell id='zd-checkbox' component='th' scope='row'>
                                            <Box display="flex" justifyContent="center">
                                                <Checkbox
                                                    checked={checkboxState.checked || false}
                                                    indeterminate={checkboxState.indeterminate || false}
                                                    onChange={(event) => handleZoneCheckbox(zd.name, event.target.checked)}
                                                />
                                            </Box>
                                        </TableCell>

                                        <TableCell
                                            id='zd-name'
                                            component='th'
                                            scope='row'
                                            sx={{ borderLeft: (theme) => getBorder(theme.palette.mode), width: '400px', minWidth: '350px' }}
                                            size='medium'
                                        >
                                            {/* {zd.name} */}
                                            {getColorByIndex(zd.data)}
                                            {/* <Chip
                                                label={zd.name}
                                                color={getColorByUuid(zd.uuid)}
                                                size='medium'
                                            /> */}
                                        </TableCell>

                                        {overtimes.map((overtime) => {
                                            const cell = crossCells.find(
                                                cell => cell.zone_deviation === zd.name && cell.overtime === overtime.name
                                            )

                                            return (
                                                <CrossCell
                                                    key={`${zd.uuid}-${overtime.uuid}`}
                                                    cell={cell}
                                                    crossCells={crossCells}
                                                    setCrossCells={setCrossCells}
                                                    loading={loadingGettingPaycodes}
                                                    setOpen={setOpen}
                                                    setRight={setRight}
                                                    setCellChecked={setChecked}
                                                />
                                            )
                                        })}
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>

            <AddPaycode
                open={open}
                setOpen={setOpen}
                loading={loadingGettingPaycodes}
                allItems={paycodes}
                right={right}
                setRight={setRight}
                updateRows={updateRows}
            />
        </>
    )
}

export { AssignmentTable }